import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, ShoppingCart, CreditCard, Navigation, CheckCircle, Timer, XCircle, AlertTriangle, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STATUS_CONFIG = {
  completed: { label: 'مكتملة', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' },
  active:    { label: 'قيد الزيارة', icon: Timer, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  planned:   { label: 'مخططة', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  cancelled: { label: 'ملغاة', icon: XCircle, color: 'text-red-500', bg: 'bg-red-50 border-red-200' },
  missed:    { label: 'مفقودة', icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-50 border-orange-200' },
};

const OUTCOME_MAP = {
  order: { label: 'طلب', color: 'bg-blue-100 text-blue-700' },
  collection: { label: 'تحصيل', color: 'bg-emerald-100 text-emerald-700' },
  order_collection: { label: 'طلب + تحصيل', color: 'bg-indigo-100 text-indigo-700' },
  info_only: { label: 'متابعة', color: 'bg-amber-100 text-amber-700' },
  no_sale: { label: 'بدون نتيجة', color: 'bg-gray-100 text-gray-600' },
  issue: { label: 'مشكلة', color: 'bg-red-100 text-red-700' },
};

function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' });
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('ar-EG', { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function VisitCard({ visit, onStartVisit, showRep = false }) {
  const navigate = useNavigate();
  const status = STATUS_CONFIG[visit.status] || STATUS_CONFIG.planned;
  const StatusIcon = status.icon;
  const outcome = OUTCOME_MAP[visit.outcome];

  return (
    <div className={`bg-card border-2 rounded-2xl p-4 transition-all ${status.bg}`}>
      {/* Header row */}
      <div className="flex items-start justify-between mb-2">
        <div className={`flex items-center gap-1.5 text-xs font-bold ${status.color}`}>
          <StatusIcon className="w-3.5 h-3.5" />
          {status.label}
          {visit.gps_verified && (
            <span className="ml-1 text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full text-xs">📍 GPS ✓</span>
          )}
        </div>
        <span className="text-xs text-muted-foreground text-left leading-tight">
          <span className="flex items-center justify-end gap-1 font-semibold text-foreground">
            <Clock className="w-3 h-3" />{fmtDate(visit.visit_date)}
          </span>
          <span className="block">
            {visit.check_in_time ? fmtTime(visit.check_in_time) : fmtTime(visit.visit_date)}
            {visit.check_out_time && ` — ${fmtTime(visit.check_out_time)}`}
          </span>
        </span>
      </div>

      {/* Customer */}
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-primary font-black text-sm">{visit.customer_name?.charAt(0)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-foreground truncate">{visit.customer_name}</p>
          {showRep && visit.salesman_name && (
            <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 font-semibold inline-block">🧭 {visit.salesman_name}</span>
          )}
          {visit.location_address && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3 flex-shrink-0" />{visit.location_address}
            </p>
          )}
        </div>
        {visit.latitude ? (
          <a
            href={`https://www.google.com/maps?q=${visit.latitude},${visit.longitude}`}
            target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            title="افتح موقع الزيارة على الخريطة"
            className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center border ${visit.status === 'active' ? 'bg-blue-600 text-white border-blue-600 animate-pulse' : 'bg-primary/10 text-primary border-primary/20'}`}>
            <Navigation className="w-4 h-4" />
          </a>
        ) : null}
        {outcome && visit.status === 'completed' && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${outcome.color}`}>{outcome.label}</span>
        )}
      </div>

      {/* Stats row */}
      {visit.status === 'completed' && (
        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mb-3">
          {visit.duration_minutes > 0 && (
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{visit.duration_minutes} دقيقة</span>
          )}
          {visit.related_order_id && (
            <span className="flex items-center gap-1 text-blue-600"><ShoppingCart className="w-3 h-3" />طلب</span>
          )}
          {visit.related_collection_id && (
            <span className="flex items-center gap-1 text-emerald-600"><CreditCard className="w-3 h-3" />تحصيل</span>
          )}
          {visit.visit_score > 0 && (
            <span className={`font-bold ${visit.visit_score >= 70 ? 'text-emerald-600' : visit.visit_score >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
              نقاط: {visit.visit_score}
            </span>
          )}
        </div>
      )}

      {visit.notes && visit.status === 'completed' && (
        <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg px-2 py-1.5 line-clamp-2 mb-2">{visit.notes}</p>
      )}

      {/* Actions */}
      <div className="flex gap-2 mt-1">
        {visit.status === 'planned' && (
          <>
            <Button onClick={() => onStartVisit(visit)} size="sm"
              className="flex-1 bg-primary text-white rounded-xl h-8 text-xs gap-1">
              <Play className="w-3 h-3" /> بدء الزيارة
            </Button>
            <Button onClick={() => navigate(`/visits/new?visit_id=${visit.id}&customer_id=${visit.customer_id}&customer_name=${encodeURIComponent(visit.customer_name)}`)}
              size="sm" variant="outline" className="rounded-xl h-8 px-2">
              <Navigation className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
        {visit.status === 'active' && (
          <Button onClick={() => navigate(`/visits/${visit.id}/continue`)} size="sm"
            className="flex-1 bg-blue-500 text-white rounded-xl h-8 text-xs">
            متابعة الزيارة الجارية
          </Button>
        )}
        {visit.status === 'completed' && (
          <Button onClick={() => navigate(`/visits/${visit.id}`)} size="sm" variant="outline"
            className="flex-1 rounded-xl h-8 text-xs">
            عرض التفاصيل
          </Button>
        )}
      </div>
    </div>
  );
}